import { getAccessToken, getScopeHeaders } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_IDENTITY_API ?? '/api/v1';

export type MembershipStatus = 'pending' | 'active' | 'suspended' | 'archived' | 'removed' | 'expired' | 'rejected';
export type ScopeType = 'tenant' | 'organization' | 'workspace';

export type Scope = {
  type: ScopeType;
  tenant_id: string;
  organization_id?: string;
  workspace_id?: string;
};

export type Membership = {
  id: string;
  identity_id: string;
  scope: Scope;
  status: MembershipStatus;
  is_primary?: boolean;
  is_default_workspace?: boolean;
  metadata?: Record<string, string>;
  settings?: Record<string, string>;
  created_at: string;
  updated_at: string;
  expires_at?: string;
};

export type Invitation = {
  id: string;
  email?: string;
  identity_id?: string;
  scope: Scope;
  status: string;
  invited_by: string;
  message?: string;
  created_at: string;
  expires_at: string;
};

function headers(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...getScopeHeaders(),
    ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}),
  };
}

async function memFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers: { ...headers(), ...(init?.headers as Record<string, string> | undefined) } });
  if (!res.ok) throw new Error(`Memberships ${path} failed: ${res.status} ${await res.text()}`);
  const body = await res.json();
  return (body.data ?? body) as T;
}

export const membershipApi = {
  list: (params?: { identity_id?: string; scope_type?: string; status?: string; q?: string }) => {
    const qs = new URLSearchParams();
    if (params?.identity_id) qs.set('identity_id', params.identity_id);
    if (params?.scope_type) qs.set('scope_type', params.scope_type);
    if (params?.status) qs.set('status', params.status);
    if (params?.q) qs.set('q', params.q);
    const query = qs.toString();
    return memFetch<Membership[]>(`/memberships${query ? `?${query}` : ''}`);
  },
  me: () => memFetch<Membership[]>('/memberships/me'),
  get: (id: string) => memFetch<Membership>(`/memberships/${id}`),
  assign: (input: { identity_id: string; scope: Scope; is_primary?: boolean }) =>
    memFetch<Membership>('/memberships', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, input: Partial<Membership>) =>
    memFetch<Membership>(`/memberships/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
  suspend: (id: string) => memFetch<Membership>(`/memberships/${id}/suspend`, { method: 'POST' }),
  restore: (id: string) => memFetch<Membership>(`/memberships/${id}/restore`, { method: 'POST' }),
  activate: (id: string) => memFetch<Membership>(`/memberships/${id}/activate`, { method: 'POST' }),
  archive: (id: string) => memFetch<Membership>(`/memberships/${id}/archive`, { method: 'POST' }),
  remove: (id: string) => memFetch<Membership>(`/memberships/${id}`, { method: 'DELETE' }),
  setPrimary: (id: string) => memFetch<Membership>(`/memberships/${id}/primary`, { method: 'POST' }),
  switch: (membershipId: string) =>
    memFetch<Membership>('/memberships/switch', { method: 'POST', body: JSON.stringify({ membership_id: membershipId }) }),
};

export const invitationApi = {
  list: (params?: { email?: string; status?: string }) => {
    const qs = new URLSearchParams(params as Record<string, string>);
    const query = qs.toString();
    return memFetch<Invitation[]>(`/invitations${query ? `?${query}` : ''}`);
  },
  create: (input: { email?: string; identity_id?: string; scope: Scope; message?: string }) =>
    memFetch<Invitation>('/invitations', { method: 'POST', body: JSON.stringify(input) }),
  accept: (id: string) => memFetch<Membership>(`/invitations/${id}/accept`, { method: 'POST' }),
  reject: (id: string) => memFetch<Invitation>(`/invitations/${id}/reject`, { method: 'POST' }),
  resend: (id: string) => memFetch<Invitation>(`/invitations/${id}/resend`, { method: 'POST' }),
  cancel: (id: string) => memFetch<Invitation>(`/invitations/${id}`, { method: 'DELETE' }),
  bulk: (invitations: Array<{ email?: string; identity_id?: string; scope: Scope; message?: string }>) =>
    memFetch<{ created: Invitation[]; failed?: Array<{ index: number; message: string }> }>('/invitations/bulk', {
      method: 'POST',
      body: JSON.stringify({ invitations }),
    }),
};
